import React, {useEffect, useRef, useState} from 'react';
import {parseAddressComponents} from "btdt/shared";
import {object, string} from 'yup';
import LocationContact from "btdt/react/LocationContact";

export default function ContactStep({simpleMode, animating, place, projectType, userInfo, chosenLocation, chosenProduct, handleInfoChange, handleNextStep}) {

    const dateField = useRef(null);

    const addressComponents = parseAddressComponents(place?.address_components)

    const [name, setName] = useState(userInfo?.name || '');
    const [phone, setPhone] = useState(userInfo?.phone || '');
    const [email, setEmail] = useState(userInfo?.email || '');
    const [country, setCountry] = useState(userInfo?.country || addressComponents?.country || 'us');
    const [street, setStreet] = useState(userInfo?.address || addressComponents?.address || '');
    const [city, setCity] = useState(userInfo?.city || addressComponents?.city || '');
    const [region, setRegion] = useState(userInfo?.region || addressComponents?.region || '');
    const [postalCode, setPostalCode] = useState(userInfo?.postalCode || addressComponents?.postalCode || '');
    const [date, setDate] = useState('');
    const [dropOff, setDropOff] = useState(userInfo?.dropOff || '');
    const [existingCustomer, setExistingCustomer] = useState(userInfo?.existingCustomer || false);
    const [valid, setValid] = useState(false);

    let EMAIL_REGX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;


    const schemaObj = {
        email: string().matches(EMAIL_REGX, "Invalid email address").required(),
    }

    if (!chosenLocation.addr_not_required) {
        schemaObj['country'] = string().required()
        schemaObj['street'] = string().required()
        schemaObj['city'] = string().required()
        schemaObj['region'] = string().required()
        schemaObj['postalCode'] = string().required()
    }

    if (chosenLocation?.validation?.name_required) {
        schemaObj['name'] = string().required()
    }

    if (chosenLocation?.validation?.phone_required) {
        schemaObj['phone'] = string().required()
    }

    if (chosenLocation?.validation?.date_required) {
        schemaObj['date'] = string().required()
    }

    if (chosenLocation?.validation?.special_required) {
        schemaObj['dropOff'] = string().required()
    }

    const contactSchema = object(schemaObj);

    const handlePhoneChange = (e) => {

        const digits = e.target.value.replace(/\D/g, '').substring(0, 10);
        const areaCode = digits.substring(0, 3);
        const prefix = digits.substring(3, 6);
        const suffix = digits.substring(6, 10);

        let phoneNum = ''
        if (digits.length > 6) {
            phoneNum = `${areaCode}-${prefix}-${suffix}`;
        } else if (digits.length > 3) {
            phoneNum = `${areaCode}-${prefix}`;
        } else if (digits.length > 0) {
            phoneNum = `${areaCode}`;
        }

        setPhone(phoneNum);
    }

    const validate = () => {
        const info = {name, phone, email, country, street, city, region, postalCode, date, dropOff, existingCustomer}
        handleInfoChange(info)
        const formValid = contactSchema.isValidSync(info)
        setValid(formValid && chosenProduct);
    }

    const RequiredLabel = () => <span className={'tiny-p font-normal text-brand-dark/75'}>&nbsp;(Required)</span>

    useEffect(() => {
        validate()
    }, [name, phone, email, country, street, city, region, postalCode, date, dropOff, existingCustomer, chosenProduct]);

    useEffect(() => {

        if (dateField.current) {

            // convert the days of the week to integers
            const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
            const hours = chosenLocation.hours.days.map(day => ({day: days.findIndex(d => d === day.day), open: day.open}))

            window.jQuery(dateField.current).datepicker({
                firstDay: 0,
                minDate: chosenLocation.allow_same_day ? 0 : 1,
                onSelect: (dateText) => {
                    setDate(dateText);
                    validate()
                },
                beforeShowDay: (date) => {
                    const foundDay = hours.find(d => d.day === date.getDay())
                    return [foundDay?.open, '', foundDay?.open ? 'Open' : 'Closed']
                }
            });
        }
    }, []);

    return (<div className={"container mx-auto px-4 lg:px-8"}>
            <div className={"grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-6"}>
                <div className={"lg:col-span-8 lg:col-start-3"}>
                    <div className={"large-p"}>
                        {projectType?.id === 'heavy' ? (<>
                                <strong>Your selection requires our heavy materials bin!</strong> Provide us with your
                                contact details and one of our friendly Dumpster Consultants will be in touch to confirm
                                the dumpster size that works for your project and materials.
                            </>) : (<>
                                Please provide your contact and delivery details:
                            </>)}
                    </div>
                </div>
                <div className={"lg:col-span-4 even:lg:col-start-3"}>
                    <label htmlFor="contact-name" className={"form-label"}>
                        Name {contactSchema.fields.name && <RequiredLabel/>}
                    </label>
                    <input type="text" id={"contact-name"} className={"form-input-text w-full bg-transparent"} value={name} onChange={e => setName(e.target.value)}/>
                </div>
                <div className={"lg:col-span-4 even:lg:col-start-3"}>
                    <label htmlFor="contact-phone" className={"form-label"}>
                        Phone Number {contactSchema.fields.phone && <RequiredLabel/>}
                    </label>
                    <input type="text" id={"contact-phone"} className={"form-input-text w-full bg-transparent"} value={phone} onChange={handlePhoneChange}/>
                </div>
                <div className={"lg:col-span-4 even:lg:col-start-3"}>
                    <label htmlFor="contact-email" className={"form-label"}>
                        Email Address {contactSchema.fields.email && <RequiredLabel/>}
                    </label>
                    <input type="text" id={"contact-email"} className={"form-input-text w-full bg-transparent"} value={email} onChange={e => setEmail(e.target.value)}/>
                </div>
                <div className={"lg:col-span-4 even:lg:col-start-3"}>
                    <label htmlFor="contact-country" className={"form-label"}>
                        Country {contactSchema.fields.country && <RequiredLabel/>}
                    </label>
                    <select id={"contact-country"} className={"form-input-text form-select w-full bg-transparent"} value={country} onChange={e => setCountry(e.target.value)}>
                        <option value="us">United States</option>
                        <option value="ca">Canada</option>
                    </select>
                </div>
                <div className={"lg:col-span-4 even:lg:col-start-3"}>
                    <label htmlFor="contact-street" className={"form-label"}>
                        Street Address {contactSchema.fields.street && <RequiredLabel/>}
                    </label>
                    <input type="text" id={"contact-street"} className={"form-input-text w-full bg-transparent"} value={street} onChange={e => setStreet(e.target.value)}/>
                </div>
                <div className={"lg:col-span-4 even:lg:col-start-3"}>
                    <label htmlFor="contact-city" className={"form-label"}>
                        City {contactSchema.fields.city && <RequiredLabel/>}
                    </label>
                    <input type="text" id={"contact-city"} className={"form-input-text w-full bg-transparent"} value={city} onChange={e => setCity(e.target.value)}/>
                </div>
                <div className={"lg:col-span-4 even:lg:col-start-3"}>
                    <label htmlFor="contact-region" className={"form-label"}>
                        State/Province {contactSchema.fields.region && <RequiredLabel/>}
                    </label>
                    <input type="text" id={"contact-region"} className={"form-input-text w-full bg-transparent"} value={region} onChange={e => setRegion(e.target.value)}/>
                </div>
                <div className={"lg:col-span-4 even:lg:col-start-3"}>
                    <label htmlFor="contact-postal" className={"form-label"}>
                        Zip/Postal Code {contactSchema.fields.postalCode && <RequiredLabel/>}
                    </label>
                    <input type="text" id="contact-postal" className={"form-input-text w-full bg-transparent"} value={postalCode} onChange={e => setPostalCode(e.target.value)}/>
                </div>
                {projectType?.id !== 'heavy' && (
                    <div className={"lg:col-span-4 even:lg:col-start-3"}>
                        <label htmlFor="contact-date" className={"form-label"}>
                            Date Needed {contactSchema.fields.date && <RequiredLabel/>}
                        </label>
                        <input type="text" ref={dateField} id={"contact-date"} className={"form-input-text date-field w-full bg-transparent"} value={date} onChange={e => setDate(e.target.value)}/>
                    </div>
                )}
                <div className={"lg:col-span-4 even:lg:col-start-3"}>
                    <label htmlFor="contact-dropoff" className={"form-label"}>
                        Special/Placement Instructions {contactSchema.fields.dropOff && <RequiredLabel/>}
                    </label>
                    <input type="text" id={"contact-dropoff"} className={"form-input-text w-full bg-transparent placeholder:text-brand-dark/70 placeholder:italic"} value={dropOff} onChange={e => setDropOff(e.target.value)} placeholder={"e.g. driveway"}/>
                </div>
                {chosenLocation.show_existing_cus && (
                    <div className={"lg:col-span-4 even:lg:col-start-3"}>
                        <div className={"radio-option"}>
                            <input type="checkbox" name={'existing-customer'} id={`existing-customer`} value={1} className={"peer"} checked={existingCustomer} onChange={(e) => setExistingCustomer(e.target.checked)}/>
                            <label htmlFor={`existing-customer`} className={"check peer-checked:bg-brand-green-light"}>
                                I am an existing customer
                            </label>
                        </div>
                    </div>
                )}

                <div className={"lg:col-span-8 lg:col-start-3"}>
                    <button className={"btn"} disabled={animating || !valid} onClick={handleNextStep}>
                        {chosenLocation.show_billing && !simpleMode ? 'Next Step →' : 'Submit Request'}
                    </button>
                </div>

                {!simpleMode && (<div className={"lg:col-span-8 lg:col-start-3"}>
                        <LocationContact location={chosenLocation}/>
                    </div>)}
            </div>
        </div>)
}
